import { useInstallationsState } from "@client-shared/providers/installations";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/** E-commerce platforms a roaster can connect as their store */
export const STORE_PLATFORMS = ["woocommerce", "shopify"] as const;

export type StorePlatform = (typeof STORE_PLATFORMS)[number];

export interface StorePlatformResult {
  /** The connected store platform, or undefined when no store installation is active */
  readonly platform: StorePlatform | undefined;
  /** Branded label used across the UI ("Shopify", "WooCommerce"); undefined when no store is connected */
  readonly label: string | undefined;
}

/** Picks the store platform from a list of installation service ids (e.g. "shopify", "woocommerce") */
export const findStorePlatform = (serviceIds: readonly string[]): StorePlatform | undefined => {
  for (const serviceId of serviceIds) {
    const normalized = serviceId.toLowerCase();
    const platform = STORE_PLATFORMS.find((candidate) => normalized.includes(candidate));
    if (platform) return platform;
  }
  return undefined;
};

/**
 * The vendor's connected store platform, derived from their ACTIVE installations,
 * with the same branded label the products table shows for that source.
 */
export function useStorePlatform(): StorePlatformResult {
  const { t } = useTranslation();
  const [{ installations }] = useInstallationsState();

  const platform = useMemo(() => {
    const activeServiceIds = Array.from(installations?.values() ?? [])
      .filter((installation) => installation.state === "ACTIVE")
      .map((installation) => installation.serviceId);
    return findStorePlatform(activeServiceIds);
  }, [installations]);

  return useMemo(
    () => ({ platform, label: platform ? (t(`views.productsTable.source.${platform}`) as string) : undefined }),
    [platform, t]
  );
}
